import Stepper from '@components/system/Stepper/Stepper';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ScheduleAppointmentSlot, {
  ISelectedValue,
  IAppointmentType,
  IProvider
} from 'src/modules/appointment-schedular/sub-modules/ScheduleAppointmentSlot/ScheduleAppointmentSlot';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import HelperUtils from '@shared/utils/HelperUtils';
import moment from 'moment';
import './ScheduleAppointment.scss';
import {useSelector} from 'react-redux';
import {State} from '@shared/store/reducers/Index';
import ScheduledAppointmentConfirmation from './ScheduledAppointmentConfirmation/ScheduledAppointmentConfirmation';
import {IPatient} from '../../TicketingSystem/TicketingSystemGrid/TicketingSystemTicketDetails/TicketingSystemTicketDetails';
import {getAllPatients} from '@shared/services/PatientsServices';
import {Patient} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import {DateFormatYYYYMMDD} from '@shared/utils/DateTimeUtils';
import {
  createAppointment,
  getAllClinicVaccines,
  getAppointmentSlots,
  getAppointmentTypes,
  getAppointmentProviders
} from '@shared/services/AppointmentsServices';
import {Vaccine} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import {getGroupLevelHolidaysList} from '@shared/services/HolidaysListService';

interface IScheduleAppointmentProps {
  close: () => void;
  clinicId: number;
  clinicGroupId: number;
  /** When set, patient dropdown is hidden and this patient is used. Used when opening from patient table. */
  preselectedPatient?: Patient | null;
  /** When set, called after appointment is booked (e.g. to navigate to Appointments screen). */
  onAppointmentBooked?: () => void;
}

const ScheduleAppointment = (
  props: IScheduleAppointmentProps
): React.ReactElement => {
  const {
    close,
    clinicId,
    clinicGroupId,
    preselectedPatient,
    onAppointmentBooked
  } = props;
  const {t} = useTranslation();

  const [
    appointmentSlotSelectedValues,
    setAppointmentSlotSelectedValues
  ] = useState<ISelectedValue>({
    appointmentDate: new Date(),
    slot: null,
    vaccine: null,
    preferredTimeActiveTabIdx: 0,
    loadingAppointmentSlots: false,
    patient: preselectedPatient ?? null,
    availableSlots: [],
    busySlots: [],
    dayWindow: null,
    holidays: [],
    appointmentType: null,
    provider: null
  });

  const {enqueueSnackbar, closeSnackbar} = useSnackbar();

  const clinicList = useSelector((state: State) => state.clinics.list || []);

  const [schedulingAppointmentState, setSchedulingAppointmentState] = useState({
    isSchedulingAppointment: false
  });

  const scheduleAnAppointment = async () => {
    if (
      !clinicId ||
      !appointmentSlotSelectedValues?.patient?.id ||
      (!appointmentSlotSelectedValues?.vaccine &&
        !appointmentSlotSelectedValues?.provider) ||
      !appointmentSlotSelectedValues?.slot
    ) {
      return;
    }
    let clinicTimeZone = 'America/Chicago';
    if (Array.isArray(clinicList)) {
      const selectedClinicDetails = clinicList.find(c => c.id == clinicId);
      if (selectedClinicDetails?.zone) {
        clinicTimeZone = selectedClinicDetails?.zone;
      }
    }
    try {
      setSchedulingAppointmentState(p => ({
        ...p,
        isSchedulingAppointment: true
      }));
      const appointmentDate = moment(
        appointmentSlotSelectedValues.appointmentDate
      ).startOf('day');
      const time = moment(
        appointmentSlotSelectedValues.slot.start_time,
        'hh:mm A'
      );
      const appointmentDateTime = appointmentDate
        .hour(time.hours())
        .minute(time.minutes())
        .format('MM/DD/yyyy HH:mm');
      const appointmentDateWithClinicTimeZone = HelperUtils.addTimeZoneToDate(
        appointmentDateTime,
        'MM/DD/yyyy HH:mm',
        clinicTimeZone
      );
      await createAppointment({
        clinic_group_id: clinicGroupId,
        clinic_id: clinicId,
        payload: {
          appointment_date: appointmentDateWithClinicTimeZone,
          patient_id: appointmentSlotSelectedValues?.patient.id,
          vaccine_id: appointmentSlotSelectedValues?.vaccine?.id,
          duration: appointmentSlotSelectedValues.slot?.duration || 15,
          provider_id: appointmentSlotSelectedValues.provider?.id,
          appointment_type_id: appointmentSlotSelectedValues.appointmentType?.id
        }
      });
    } finally {
      setSchedulingAppointmentState(p => ({
        ...p,
        isSchedulingAppointment: false
      }));
    }
  };

  const fetchAllPatientsWithMatchingText = async (
    searchText: string
  ): Promise<any[] | undefined> => {
    if (!clinicId || !clinicGroupId) return [] as IPatient[];
    try {
      if (searchText?.length < 3) return [] as IPatient[];
      const params: Parameters<typeof getAllPatients>['0']['params'] = {};
      if (clinicId) {
        params.clinic_id = clinicId;
      }
      params.patient_name = searchText;
      const response = await getAllPatients({
        clinic_group_id: clinicGroupId,
        params
      });

      if (response.data.rows) {
        return response.data.rows as IPatient[];
      }
      return [] as IPatient[];
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  const [availableVaccinesList, setAvailableVaccinesList] = useState<Vaccine[]>(
    []
  );

  const [appointmentTypesList, setAppointmentTypesList] = useState<
    IAppointmentType[]
  >([]);

  const [availableProvidersList, setAvailableProvidersList] = useState<
    IProvider[]
  >([]);
  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      if (!clinicId || !clinicGroupId) return;
      try {
        const res = await getAppointmentTypes(clinicGroupId, clinicId);
        if (res.data && Array.isArray(res.data)) {
          setAppointmentTypesList(res.data);

          if (
            res.data.length === 1 &&
            !appointmentSlotSelectedValues.appointmentType
          ) {
            setAppointmentSlotSelectedValues(prevState => ({
              ...prevState,
              appointmentType: res.data[0],
              slot: null,
              availableSlots: [],
              busySlots: [],
              dayWindow: null,
              vaccine: null,
              provider: null
            }));
          }
        } else {
          setAppointmentTypesList([]);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    fetchAppointmentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, clinicGroupId, enqueueSnackbar]);

  useEffect(() => {
    const fetchProviders = async () => {
      if (
        !clinicId ||
        !clinicGroupId ||
        !appointmentSlotSelectedValues.appointmentType?.id ||
        !appointmentSlotSelectedValues.appointmentType.is_provider_required
      ) {
        setAvailableProvidersList([]);
        return;
      }

      try {
        const res = await getAppointmentProviders(
          clinicGroupId,
          clinicId,
          appointmentSlotSelectedValues.appointmentType.id
        );
        if (res.data && Array.isArray(res.data)) {
          setAvailableProvidersList(res.data as IProvider[]);
        } else {
          setAvailableProvidersList([]);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setAvailableProvidersList([]);
      }
    };

    if (appointmentSlotSelectedValues.provider) {
      setAppointmentSlotSelectedValues(p => ({...p, provider: null}));
      return;
    }

    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentSlotSelectedValues.appointmentType,
    // appointmentSlotSelectedValues.provider,
    clinicId,
    clinicGroupId,
    enqueueSnackbar
  ]);

  useEffect(() => {
    const fetchAppointmentSlot = async () => {
      const hasRequiredFilters =
        !!clinicId &&
        !!clinicGroupId &&
        !!appointmentSlotSelectedValues.appointmentDate &&
        !!appointmentSlotSelectedValues.appointmentType?.id &&
        !!appointmentSlotSelectedValues.patient?.id &&
        (!!appointmentSlotSelectedValues.vaccine?.id ||
          !!appointmentSlotSelectedValues.provider?.id);

      if (!hasRequiredFilters) {
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          availableSlots: [],
          busySlots: [],
          dayWindow: null,
          slot: null,
          loadingAppointmentSlots: false
        }));
        return;
      }
      try {
        const formattedDate = DateFormatYYYYMMDD(
          appointmentSlotSelectedValues.appointmentDate
        );
        closeSnackbar();
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          loadingAppointmentSlots: true,
          availableSlots: [],
          busySlots: [],
          dayWindow: null,
          slot: null
        }));
        const params: {
          date: string;
          appointment_type_id: number;
          patient_id: number;
          vaccine_id?: number;
          provider_id?: number;
        } = {
          date: formattedDate,
          appointment_type_id: appointmentSlotSelectedValues.appointmentType!.id,
          patient_id: appointmentSlotSelectedValues.patient!.id
        };

        if (appointmentSlotSelectedValues.vaccine?.id) {
          params.vaccine_id = appointmentSlotSelectedValues.vaccine.id;
        }
        if (appointmentSlotSelectedValues.provider?.id) {
          params.provider_id = appointmentSlotSelectedValues.provider.id;
        }

        const res = await getAppointmentSlots({
          clinic_group_id: clinicGroupId,
          clinic_id: clinicId,
          params
        });
        if (Array.isArray(res.data?.rows)) {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            availableSlots: res.data.rows,
            busySlots: Array.isArray(res.data.busy_slots)
              ? res.data.busy_slots
              : [],
            dayWindow: res.data.day_window || null
          }));
        } else {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            availableSlots: [],
            busySlots: [],
            dayWindow: null,
            slot: null
          }));
        }
      } catch (err: any) {
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          availableSlots: [],
          busySlots: [],
          dayWindow: null,
          slot: null
        }));
        enqueueSnackbar(
          err?.message
            ? err.message
            : t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.ERROR_GETTING_APPOINTMENT_SLOTS'
              ),
          SnackBarConfig('e')
        );
      } finally {
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          loadingAppointmentSlots: false
        }));
      }
    };
    fetchAppointmentSlot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentSlotSelectedValues.appointmentDate,
    appointmentSlotSelectedValues.vaccine,
    appointmentSlotSelectedValues.patient,
    appointmentSlotSelectedValues.provider,
    appointmentSlotSelectedValues.appointmentType,
    clinicGroupId,
    clinicId
  ]);

  useEffect(() => {
    const fetchHolidays = async () => {
      if (!clinicGroupId && !clinicId) return;
      try {
        const res = await getGroupLevelHolidaysList(clinicGroupId, {
          include_system_holidays: true,
          clinic_id: clinicId
        });
        if (Array.isArray(res.data.holidays)) {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            holidays: res.data.holidays.map((i: any) => ({
              date: moment(i.holiday_on).format('YYYY-MM-DD'),
              description: i.description || null
            }))
          }));
        } else {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            holidays: []
          }));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    fetchHolidays();
  }, [clinicId, clinicGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllVaccines = async () => {
    if (!clinicId || !clinicGroupId) return;
    try {
      const vaccinesResponse = await getAllClinicVaccines({
        clinic_group_id: clinicGroupId,
        clinic_id: clinicId,
        appointment_type: appointmentSlotSelectedValues.appointmentType?.name_id
      });
      if (Array.isArray(vaccinesResponse.data)) {
        setAvailableVaccinesList(vaccinesResponse.data);
        if (
          vaccinesResponse.data.length === 1 &&
          !appointmentSlotSelectedValues.vaccine
        ) {
          setAppointmentSlotSelectedValues(prevState => ({
            ...prevState,
            vaccine: vaccinesResponse.data[0]
          }));
        }
      } else {
        setAvailableVaccinesList([]);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  useEffect(() => {
    fetchAllVaccines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentSlotSelectedValues?.appointmentType]);

  return (
    <div className="schedule-appointment-wrapper">
      <Stepper
        steps={[
          {
            content: (
              <ScheduleAppointmentSlot
                selectedValues={appointmentSlotSelectedValues}
                updateSelectedValues={setAppointmentSlotSelectedValues}
                fetchPatients={fetchAllPatientsWithMatchingText}
                availableVaccinesList={availableVaccinesList}
                appointmentTypesList={appointmentTypesList}
                availableProvidersList={availableProvidersList}
                hidePatientDropdown={!!preselectedPatient}
              />
            ),
            title: t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.SELECT_APPOINTMENT_SLOT_STEP_LABEL'
            ),
            nextButtonLabel: t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.CONFIRM_APPOINTMENT'
            ),
            backButtonLabel: t('COMMON.CANCEL'),
            onBackButtonClick: () => {
              close();
              return false;
            },
            nextButtonDisabledState:
              !appointmentSlotSelectedValues.appointmentDate ||
              !appointmentSlotSelectedValues.slot ||
              !appointmentSlotSelectedValues.patient?.id ||
              !appointmentSlotSelectedValues.appointmentType?.id ||
              (!appointmentSlotSelectedValues.vaccine?.id &&
                !appointmentSlotSelectedValues.provider?.id) ||
              appointmentSlotSelectedValues.holidays.some(
                holiday =>
                  holiday.date ===
                  moment(appointmentSlotSelectedValues.appointmentDate).format(
                    'YYYY-MM-DD'
                  )
              ),

            onNextButtonClick: async () => {
              try {
                await scheduleAnAppointment();
                return true;
              } catch (err: any) {
                enqueueSnackbar(err?.message ?? '', SnackBarConfig('e'));
                return false;
              }
            },
            nextButtonLoadingState:
              schedulingAppointmentState.isSchedulingAppointment
          },
          {
            title: t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.APPOINTMENT_SCHEDULED'
            ),
            content: (
              <ScheduledAppointmentConfirmation
                appointmentSlotSelectedValues={appointmentSlotSelectedValues}
              />
            ),
            nextButtonLabel: t('COMMON.CLOSE'),
            onNextButtonClick() {
              onAppointmentBooked?.();
              close();
              return false;
            },
            hideBackButton: true
          }
        ]}
      />
    </div>
  );
};

export default ScheduleAppointment;
